import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../components/Theme";
import GetEquipmentStock from "../../api/stock/GetEquipmentStock";
import CreateEquipmentStock from "../../api/stock/CreateEquipmentStock";
import DeleteEquipmentStock from "../../api/stock/DeleteEquipmentStock";

const categoryOptions = {
  1: "รถ",
  2: "เครื่องมือ",
  3: "อุปกรณ์",
  4: "ไม่ระบุ",
};

export default function EquipmentStockTable({ appCropId }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [equipStocks, setEquipStocks] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (appCropId) {
        try {
          const res = await GetEquipmentStock({ appCropId });
          setEquipStocks(res);
        } catch (err) {
          console.error("Error fetching equipment stock:", err);
        }
      }
    };
    fetchData();
  }, [appCropId]);

  const handleChange = (index, key, value) => {
    const updated = [...equipStocks];
    updated[index][key] = value;
    setEquipStocks(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    setEquipStocks([
      ...equipStocks,
      {
        appCropId,
        appEquipStockId: "SC" + timestamp,
        equipCategoryId: 4,
        equipName: "",
        unitName: "ชิ้น",
        stockAmount: 0,
        stockDate: "",
      },
    ]);
  };

  const openDeleteDialog = (id) => {
    setSelectedDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await DeleteEquipmentStock({ appEquipStockId: selectedDeleteId });
      setEquipStocks((prev) =>
        prev.filter((item) => item.appEquipStockId !== selectedDeleteId)
      );
      setSnackbarSeverity("success");
      setSnackbarMessage("ลบรายการสำเร็จ");
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาดในการลบ");
    } finally {
      setDeleteDialogOpen(false);
      setOpenSnackbar(true);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleSave = async () => {
    const results = await CreateEquipmentStock(equipStocks);
    const hasError = results.some((r) => r.status !== 200);
    if (hasError) {
      setSnackbarSeverity("error");
      setSnackbarMessage("บางรายการบันทึกไม่สำเร็จ");
    } else {
      setSnackbarSeverity("success");
      setSnackbarMessage("บันทึกสำเร็จทั้งหมด");
    }
    setOpenSnackbar(true);
  };

  return (
    <Box p={2}>
      <Box
        width="100%"
        sx={{ backgroundColor: colors.greenAccent[300] }}
        display="flex"
        justifyContent="center"
      >
        <Typography
          p={isMobile ? 1 : 2}
          variant={isMobile ? "body1" : "h6"}
          fontWeight="bold"
          color="#fff"
        >
          สต็อกอุปกรณ์
        </Typography>
      </Box>

      <Box maxWidth={isMobile ? "320px" : "none"}>
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>วันที่</TableCell>
                <TableCell>หมวดหมู่อุปกรณ์</TableCell>
                <TableCell>ชื่ออุปกรณ์</TableCell>
                <TableCell>จำนวน</TableCell>
                <TableCell align="center">ลบ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipStocks.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={row.stockDate}
                      onChange={(e) =>
                        handleChange(index, "stockDate", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={row.equipCategoryId}
                      onChange={(e) =>
                        handleChange(index, "equipCategoryId", e.target.value)
                      }
                    >
                      {Object.entries(categoryOptions).map(([id, label]) => (
                        <MenuItem key={id} value={Number(id)}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      sx={{ minWidth: "300px" }}
                      size="small"
                      value={row.equipName}
                      onChange={(e) =>
                        handleChange(index, "equipName", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      sx={{ minWidth: "120px" }}
                      size="small"
                      type="number"
                      value={row.stockAmount}
                      onChange={(e) =>
                        handleChange(index, "stockAmount", e.target.value)
                      }
                      InputProps={{
                        endAdornment: (
                          <span style={{ marginLeft: 4 }}>{row.unitName}</span>
                        ),
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteDialog(row.appEquipStockId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    variant="outlined"
                  >
                    เพิ่มแถว
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box textAlign="center" mt={3}>
        <Button
          variant="contained"
          sx={{ backgroundColor: colors.greenAccent[400] }}
          onClick={handleSave}
        >
          บันทึกข้อมูล
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            justifyContent: "center",
            minWidth: "200px",
            textAlign: "center",
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้ใช่หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={confirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
